import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { SITE, MEDIA } from "../lib/site";

export default function Hero() {
  const { t } = useLanguage();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#0A0A0A] pt-16"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${MEDIA.hero})` }}
      />
      <div className="absolute inset-0 bg-black/65" />
      <div className="absolute inset-0 grid-overlay opacity-40" />

      {/* Top meta bar */}
      <div className="absolute top-20 left-0 right-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] font-mono-tech tracking-[0.2em] uppercase text-neutral-400">
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 bg-yellow-500 animate-pulse" />
              {t.hero.overline}
            </span>
            <span className="hidden sm:inline text-neutral-700">/</span>
            <span className="hidden sm:inline">{t.hero.meta_specialty}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-screen flex flex-col justify-end pb-24 pt-40">
        <div className="grid grid-cols-12 gap-6 items-end">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="col-span-12 lg:col-span-8"
          >
            <h1
              data-testid="hero-title"
              className="font-display font-bold text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tight leading-[0.95]"
            >
              <span className="block">{t.hero.title_l1}</span>
              <span className="block text-neutral-400">{t.hero.title_l2}</span>
              <span className="block text-yellow-400 text-glow">
                {t.hero.title_l3}
              </span>
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-8 max-w-2xl text-base sm:text-lg text-neutral-300 leading-relaxed"
            >
              {t.hero.subtitle}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 btn-yellow px-6 py-3.5 text-sm font-mono-tech uppercase tracking-[0.15em]"
              >
                {t.hero.cta_primary}
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#services"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 btn-outline px-6 py-3.5 text-sm font-mono-tech uppercase tracking-[0.15em]"
              >
                {t.hero.cta_secondary}
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
            className="col-span-12 lg:col-span-4"
          >
            <div className="bg-black/60 backdrop-blur-md border border-neutral-800 p-6 space-y-4">
              <div className="overline">{SITE.role}</div>
              <div className="font-display font-bold text-2xl tracking-tight">
                {SITE.expertName}
              </div>
              <div className="section-divider" />
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <div className="font-mono-tech text-neutral-500 uppercase tracking-[0.2em] text-[10px]">
                    {t.hero.meta_auth}
                  </div>
                  <div className="font-mono-tech text-yellow-400 mt-1.5">
                    {SITE.authNumber}
                  </div>
                </div>
                <div>
                  <div className="font-mono-tech text-neutral-500 uppercase tracking-[0.2em] text-[10px]">
                    {t.hero.meta_series}
                  </div>
                  <div className="font-mono-tech text-yellow-400 mt-1.5">
                    {SITE.authSeries}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* scroll indicator */}
        <div className="mt-16 hidden sm:flex items-center gap-2 text-neutral-500 font-mono-tech text-[10px] uppercase tracking-[0.3em]">
          <ChevronDown className="h-3 w-3 animate-bounce" />
          Scroll
        </div>
      </div>
    </section>
  );
}
